import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, DollarSign } from "lucide-react";
import { Checkbox } from "../../components/ui";

const FilterModal = ({ filters, setFilters, onClose, categories, brands }) => {
  const handleCheckboxChange = (filterType, value) => {
    const updated = { ...filters };
    const i = updated[filterType].indexOf(value);
    if (i > -1) updated[filterType].splice(i, 1);
    else updated[filterType].push(value);
    setFilters(updated);
  };

  const handlePriceRangeChange = (min, max) => setFilters({ ...filters, priceRange: { min, max } });

  return (
    <AnimatePresence>
      <motion.button
        type="button"
        onClick={onClose}
        className="fixed inset-0 bg-black/40 z-40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        className="fixed z-50 inset-x-0 top-12 mx-auto w-full max-w-sm bg-surface rounded-2xl shadow-2xl border border-line overflow-hidden"
      >
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <h2 className="text-base font-semibold">Filters</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface-2" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-5 max-h-[70vh] overflow-y-auto">
          <div>
            <h3 className="text-sm font-medium mb-2 text-start">Categories</h3>
            <div className="space-y-2">
              {categories.map((c) => (
                <Checkbox
                  key={c.id}
                  className="flex"
                  label={c.name}
                  checked={filters.categories.includes(c.id)}
                  onChange={() => handleCheckboxChange("categories", c.id)}
                />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-2 text-start">Brands</h3>
            <div className="space-y-2">
              {brands.map((b) => (
                <Checkbox
                  key={b.id}
                  className="flex"
                  label={b.name}
                  checked={filters.brands.includes(b.id)}
                  onChange={() => handleCheckboxChange("brands", b.id)}
                />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-2 text-start">Status</h3>
            <div className="space-y-2">
              {["active", "inactive"].map((s) => (
                <Checkbox
                  key={s}
                  className="flex"
                  label={s === "active" ? "Active" : "Inactive"}
                  checked={filters.status.includes(s)}
                  onChange={() => handleCheckboxChange("status", s)}
                />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-2 text-start">Price Range</h3>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <DollarSign className="w-4 h-4 text-faint absolute left-2 top-1/2 -translate-y-1/2" />
                <input
                  type="number"
                  value={filters.priceRange.min}
                  onChange={(e) => handlePriceRangeChange(Number(e.target.value), filters.priceRange.max)}
                  className="pl-7 pr-2 py-2 text-sm border border-line rounded-lg w-full focus:ring-2 focus:ring-accent outline-none"
                  placeholder="Min"
                />
              </div>
              <div className="relative flex-1">
                <DollarSign className="w-4 h-4 text-faint absolute left-2 top-1/2 -translate-y-1/2" />
                <input
                  type="number"
                  value={filters.priceRange.max}
                  onChange={(e) => handlePriceRangeChange(filters.priceRange.min, Number(e.target.value))}
                  className="pl-7 pr-2 py-2 text-sm border border-line rounded-lg w-full focus:ring-2 focus:ring-accent outline-none"
                  placeholder="Max"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-2 text-start">Special Filters</h3>
            <div className="space-y-2">
              {[
                { key: "isTopSeller", label: "Top Seller" },
                { key: "isFeatured", label: "Featured" },
                { key: "isSpecialOffer", label: "Special Offer" },
              ].map(({ key, label }) => (
                <Checkbox
                  key={key}
                  className="flex"
                  label={label}
                  checked={!!filters[key]}
                  onChange={() => setFilters({ ...filters, [key]: !filters[key] })}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-line bg-surface-2 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-white bg-accent hover:opacity-90"
          >
            Apply Filters
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FilterModal;
